import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import type WaSelect from '@awesome.me/webawesome/dist/components/select/select.js';
import { nsLoaderContext, nsSettingsServiceContext } from '../ns-services/contexts.js';

import { nsSharedStyles, nsLayoutStyles } from '../ns-styles/index.js';

import { NSSettingsService } from '../ns-settings/ns-settings-service.js';
import { NSLoader } from '../ns-services/ns-loader-service.js';

import { callLambda } from '../helpers/lambda.js';
import { createLogger } from '../ns-services/logger.js';

const logger = createLogger('ns-test-persona-switcher');

interface TestPersona {
  userId: string;
  label: string;
  role: string;
  company: string | null;
}

/**
 * Dev/test/staging-only tool, embedded in `<ns-dev-tools-panel>` — lets an already-authenticated
 * user switch to one of the seeded test personas without touching devtools or raw tokens. Renders
 * nothing in a prod build or if there's nothing to show; the real gate is server-side (the
 * debug/test-personas lambdas 404 outside dev/test/staging), this is just cosmetic on top of that.
 */
@customElement('ns-test-persona-switcher')
export class NSTestPersonaSwitcher extends LitElement {
  @consume({ context: nsLoaderContext })
  private __loader?: NSLoader;

  @consume({ context: nsSettingsServiceContext })
  private __settings?: NSSettingsService;

  @state()
  private __personas: TestPersona[] = [];

  static styles = [
    nsSharedStyles,
    nsLayoutStyles,
    css`
      :host {
        display: block;
      }

      .label {
        font-size: var(--wa-font-size-s);
        margin-bottom: var(--wa-space-2xs);
      }

      select {
        width: 100%;
      }
    `,
  ];

  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    if (!this.isConnected) {
      logger.debug('Element has been disconnected before finishing the connectedCallback execution');
      return;
    }

    const settings = this.__settings;
    if (!settings) throw new Error('Settings service not found');

    const mode = (settings.settings.app as { mode?: string } | undefined)?.mode;
    if (!mode || mode === 'prod' || mode === 'production') return;

    await this.__loadPersonas();
  }

  private async __loadPersonas() {
    try {
      const token = window.sessionStorage.getItem('userToken');
      if (!token) return;

      const result = await callLambda('debug/test-personas', 'GET', token);
      const res = result.res as unknown as { personas: TestPersona[] };
      this.__personas = res.personas ?? [];
    } catch (err) {
      // Server-side gate said no (wrong env, or feature not deployed here) — nothing to show.
      logger.debug('Test persona switcher unavailable', err);
    }
  }

  private async __onSelect(ev: Event) {
    const userId = (ev.target as WaSelect).value;
    if (!userId) return;

    try {
      await this.__loader?.startLoading();

      const token = window.sessionStorage.getItem('userToken');
      const result = await callLambda(
        'debug/test-personas/switch',
        'POST',
        token ?? undefined,
        JSON.stringify({ userId }),
      );
      const res = result.res as unknown as { oauthApp: string; userToken: string };

      window.sessionStorage.setItem('userToken', res.userToken);
      window.sessionStorage.setItem('oauthApp', res.oauthApp);

      window.location.href = '/';
    } catch (err) {
      logger.error('Failed to switch test persona', err);
    }
  }

  render() {
    if (this.__personas.length === 0) return html``;

    return html`
      <div class="switcher">
        <div class="label" aria-hidden="true">Switch test user</div>
        <wa-select
          class="flex"
          label="Switch test user"
          placeholder="Please Select Someone..."
          @change=${this.__onSelect}
        >
          ${this.__personas.map(
            (persona) => html`
              <wa-option value=${persona.userId}>
                ${persona.company
                  ? `${persona.label} — ${persona.role} at ${persona.company}`
                  : `${persona.label} — ${persona.role}`}
              </wa-option>
            `,
          )}
        </wa-select>
      </div>
    `;
  }
}
